import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid,
  Paper,
  Stack,
  Skeleton,
  Drawer,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { commissionsApi } from "../../Components/commissionsApi";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import styles from "../AllEnquiries.module.scss";

const baseThemeOptions = {
  palette: { secondary: { main: "#f5365c" }, success: { main: "#2dce89" }, warning: { main: "#fb6340" }, info: { main: "#11cdef" } },
  typography: { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", button: { textTransform: "none", fontWeight: 500 } },
  shape: { borderRadius: 12 },
};

const TIMEFRAMES = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const SOURCES = [
  { value: "total_business_revenue", label: "Total Business Revenue" },
  { value: "total_conversions", label: "Total Conversions" },
  { value: "conversions_by_program", label: "Conversions by Program" },
  { value: "conversions_by_course", label: "Conversions by Course" },
];

const RULE_TYPES = [
  { value: "percentage", label: "Percentage Commission" },
  { value: "fixed_per_item", label: "Fixed Commission Per Item" },
];

const TRIGGER_EVENTS = [
  { value: "conversion_created", label: "Conversion created" },
  { value: "revenue_recorded", label: "Revenue recorded" },
  { value: "registration_completed", label: "Registration completed" },
  { value: "payment_received", label: "Payment received" },
];

const COMMISSION_MODELS = [
  { value: "progressive", label: "Progressive" },
  { value: "retroactive", label: "Retroactive" },
];

const defaultBracket = () => ({ min_value: 0, max_value: "", value: "" });
const defaultRule = () => ({ source: "total_conversions", rule_type: "fixed_per_item", scope_id: "", brackets: [defaultBracket()] });

export default function CommissionStructureForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const { themeMode } = useTheme();
  const theme = useMemo(() => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)), [themeMode]);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [timeframeConfirmOpen, setTimeframeConfirmOpen] = useState(false);
  const [pendingTimeframe, setPendingTimeframe] = useState(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [timeframe, setTimeframe] = useState("monthly");
  const [trigger_event, setTriggerEvent] = useState("conversion_created");
  const [commission_cap, setCommissionCap] = useState("");
  const [minimum_threshold, setMinimumThreshold] = useState("");
  const [commission_model, setCommissionModel] = useState("progressive");
  const [rules, setRules] = useState([defaultRule()]);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    commissionsApi
      .getById(id)
      .then((res) => {
        const s = res.structure || {};
        setName(s.name || "");
        setDescription(s.description || "");
        setTimeframe(s.timeframe || "monthly");
        setTriggerEvent(s.trigger_event || "conversion_created");
        setCommissionCap(s.commission_cap != null ? String(s.commission_cap) : "");
        setMinimumThreshold(s.minimum_threshold != null ? String(s.minimum_threshold) : "");
        setCommissionModel(s.commission_model || "progressive");
        if (Array.isArray(s.rules) && s.rules.length) {
          setRules(
            s.rules.map((r) => ({
              source: r.source || "total_conversions",
              rule_type: r.rule_type || "fixed_per_item",
              scope_id: r.scope_id != null ? String(r.scope_id) : "",
              brackets: (r.brackets || []).length ? r.brackets.map((b) => ({ min_value: b.min_value ?? 0, max_value: b.max_value ?? "", value: b.value ?? "" })) : [defaultBracket()],
            }))
          );
        }
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to load structure");
        navigate("/commissions");
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const handleTimeframeChange = (newVal) => {
    if (rules.some((r) => r.brackets.some((b) => b.value !== ""))) {
      setPendingTimeframe(newVal);
      setTimeframeConfirmOpen(true);
    } else {
      setTimeframe(newVal);
    }
  };

  const confirmTimeframeConvert = () => {
    if (pendingTimeframe) setTimeframe(pendingTimeframe);
    setPendingTimeframe(null);
    setTimeframeConfirmOpen(false);
    toast.info("Timeframe updated. You may want to review bracket values for the new period.");
  };

  const addRule = () => setRules((prev) => [...prev, defaultRule()]);
  const removeRule = (idx) => {
    if (rules.length <= 1) return;
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateRule = (idx, field, value) => {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addBracket = (ruleIdx) => {
    setRules((prev) =>
      prev.map((r, i) => (i === ruleIdx ? { ...r, brackets: [...r.brackets, defaultBracket()] } : r))
    );
  };
  const removeBracket = (ruleIdx, bracketIdx) => {
    setRules((prev) =>
      prev.map((r, i) =>
        i === ruleIdx
          ? { ...r, brackets: r.brackets.length > 1 ? r.brackets.filter((_, bi) => bi !== bracketIdx) : r.brackets }
          : r
      )
    );
  };
  const updateBracket = (ruleIdx, bracketIdx, field, value) => {
    setRules((prev) =>
      prev.map((r, i) =>
        i === ruleIdx
          ? { ...r, brackets: r.brackets.map((b, bi) => (bi === bracketIdx ? { ...b, [field]: value } : b)) }
          : r
      )
    );
  };

  const validate = () => {
    if (!name.trim()) {
      toast.error("Commission name is required.");
      return false;
    }
    for (let ri = 0; ri < rules.length; ri++) {
      const r = rules[ri];
      for (let bi = 0; bi < r.brackets.length; bi++) {
        const b = r.brackets[bi];
        const val = Number(b.value);
        if (b.value === "" || isNaN(val)) {
          toast.error(`Rule ${ri + 1} bracket ${bi + 1}: value is required.`);
          return false;
        }
      }
    }
    return true;
  };

  const buildPayload = () => {
    return {
      name: name.trim(),
      description: description.trim() || undefined,
      timeframe,
      trigger_event,
      commission_cap: commission_cap !== "" && !isNaN(Number(commission_cap)) ? Number(commission_cap) : undefined,
      minimum_threshold: minimum_threshold !== "" && !isNaN(Number(minimum_threshold)) ? Number(minimum_threshold) : undefined,
      commission_model,
      rules: rules.map((r) => ({
        source: r.source,
        rule_type: r.rule_type,
        scope_id: r.scope_id !== "" ? Number(r.scope_id) : undefined,
        brackets: r.brackets.map((b) => ({
          min_value: b.min_value === "" ? 0 : Number(b.min_value),
          max_value: b.max_value === "" ? null : Number(b.max_value),
          value: Number(b.value),
        })),
      })),
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await commissionsApi.update(id, payload);
        toast.success("Commission structure updated.");
      } else {
        await commissionsApi.create(payload);
        toast.success("Commission structure created.");
      }
      navigate("/commissions");
    } catch (err) {
      toast.error(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box className={styles.root}>
          <Paper elevation={0} className={styles.contentPaper} sx={{ p: 3 }}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
              </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper} sx={{ p: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/commissions")} sx={{ mb: 1 }}>
                  Back to Commissions
                </Button>
                <Typography variant="h4" className={styles.pageTitle}>
                  {isEdit ? "Edit Commission Structure" : "Create Commission Structure"}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Commission Name" value={name} onChange={(e) => setName(e.target.value)} required />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2} label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Timeframe</InputLabel>
                    <Select value={timeframe} label="Timeframe" onChange={(e) => handleTimeframeChange(e.target.value)}>
                      {TIMEFRAMES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Trigger Event</InputLabel>
                    <Select value={trigger_event} label="Trigger Event" onChange={(e) => setTriggerEvent(e.target.value)}>
                      {TRIGGER_EVENTS.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth type="number" label="Commission Cap (optional)" value={commission_cap} onChange={(e) => setCommissionCap(e.target.value)} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth type="number" label="Minimum Threshold (optional)" value={minimum_threshold} onChange={(e) => setMinimumThreshold(e.target.value)} inputProps={{ min: 0 }} />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Commission Model</InputLabel>
                    <Select value={commission_model} label="Commission Model" onChange={(e) => setCommissionModel(e.target.value)}>
                      {COMMISSION_MODELS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Commission Sources & Rules</Typography>
              {rules.map((rule, ri) => (
                <Card key={ri} sx={{ mb: 3 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="subtitle1">Rule {ri + 1}</Typography>
                      <Stack direction="row" spacing={1}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Source</InputLabel>
                          <Select value={rule.source} label="Source" onChange={(e) => updateRule(ri, "source", e.target.value)}>
                            {SOURCES.map((s) => (
                              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <InputLabel>Type</InputLabel>
                          <Select value={rule.rule_type} label="Type" onChange={(e) => updateRule(ri, "rule_type", e.target.value)}>
                            {RULE_TYPES.map((t) => (
                              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {rules.length > 1 && (
                          <IconButton size="small" color="error" onClick={() => removeRule(ri)}><DeleteIcon /></IconButton>
                        )}
                      </Stack>
                    </Stack>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Min</TableCell>
                          <TableCell>Max (empty = no limit)</TableCell>
                          <TableCell>{rule.rule_type === "percentage" ? "Percentage (%)" : "Amount per item"}</TableCell>
                          <TableCell width={60}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rule.brackets.map((b, bi) => (
                          <TableRow key={bi}>
                            <TableCell>
                              <TextField type="number" size="small" value={b.min_value} onChange={(e) => updateBracket(ri, bi, "min_value", e.target.value)} inputProps={{ min: 0 }} sx={{ width: 100 }} />
                            </TableCell>
                            <TableCell>
                              <TextField type="number" size="small" value={b.max_value} onChange={(e) => updateBracket(ri, bi, "max_value", e.target.value)} inputProps={{ min: 0 }} sx={{ width: 100 }} placeholder="∞" />
                            </TableCell>
                            <TableCell>
                              <TextField type="number" size="small" value={b.value} onChange={(e) => updateBracket(ri, bi, "value", e.target.value)} required inputProps={{ min: 0, step: "any" }} sx={{ width: 100 }} />
                            </TableCell>
                            <TableCell>
                              {rule.brackets.length > 1 && (
                                <IconButton size="small" onClick={() => removeBracket(ri, bi)}><DeleteIcon fontSize="small" /></IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => addBracket(ri)} sx={{ mt: 1 }}>Add bracket</Button>
                  </CardContent>
                </Card>
              ))}
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addRule} sx={{ mb: 3 }}>Add rule (source)</Button>

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                  {saving ? "Saving…" : isEdit ? "Update" : "Create"}
                </Button>
                <Button variant="outlined" onClick={() => navigate("/commissions")}>Cancel</Button>
              </Stack>
            </Paper>

        <Dialog open={timeframeConfirmOpen} onClose={() => { setTimeframeConfirmOpen(false); setPendingTimeframe(null); }}>
          <DialogTitle>Change timeframe</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Do you want to automatically convert the commission calculations to the new timeframe? You can also keep your current bracket values and adjust them manually.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setTimeframeConfirmOpen(false); setPendingTimeframe(null); }}>Cancel</Button>
            <Button variant="contained" onClick={confirmTimeframeConvert}>Yes, update timeframe</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
